import { useNavigate } from "react-router-dom";
import { Heart, Clock, Users, Utensils, AlertTriangle, Check } from "lucide-react";
import { recipeImage } from "@/lib/api";
import { cuisineOf } from "@/lib/cuisines";

export default function RecipeCard({ recipe, onToggleFav, index = 0, currentUserId }) {
  const navigate = useNavigate();
  const img = recipeImage(recipe);
  const time = (recipe.prep_time_minutes || 0) + (recipe.cook_time_minutes || 0);
  const byOther = recipe.created_by && currentUserId && recipe.created_by !== currentUserId;
  const idHash = (recipe.id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const showHeart = idHash % 3 === 0; // certaines recettes affichent un cœur, peu importe lesquelles
  const dislikedMain = recipe.disliked_main || [];
  const hasDisliked = dislikedMain.length > 0;
  const cuisine = recipe.cuisine && recipe.cuisine !== "autre" ? cuisineOf(recipe.cuisine) : null;

  return (
    <button
      data-testid={`recipe-card-${recipe.id}`}
      onClick={() => navigate(`/recipes/${recipe.id}`)}
      className="at-press at-fade-up group w-full overflow-hidden rounded-3xl border border-black/5 bg-white text-left at-shadow"
      style={{ animationDelay: `${index * 45}ms`, opacity: hasDisliked ? 0.55 : 1 }}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden" style={{ background: "var(--at-elevated)" }}>
        {img ? (
          <img src={img} alt={recipe.title} className={`h-full w-full object-contain transition-transform duration-500 group-hover:scale-105 ${hasDisliked ? "grayscale" : ""}`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Utensils size={40} style={{ color: "var(--at-muted)" }} />
          </div>
        )}
        {onToggleFav && (
          <span
            role="button"
            data-testid={`fav-toggle-${recipe.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleFav(recipe); }}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 backdrop-blur at-shadow"
          >
            <Heart size={18} fill={recipe.is_favorite ? "#E05A47" : "none"} color={recipe.is_favorite ? "#E05A47" : "#8C7B75"} />
          </span>
        )}
        {byOther && (
          <span data-testid={`shared-badge-${recipe.id}`}
            className="absolute left-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 backdrop-blur at-shadow"
            style={{ color: "var(--at-brand)" }}>
            {showHeart ? <Heart size={14} fill="#E05A47" color="#E05A47" /> : <Check size={15} strokeWidth={3} />}
          </span>
        )}
        {cuisine && (
          <span data-testid={`cuisine-badge-${recipe.id}`}
            className="absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[11px] font-bold backdrop-blur at-shadow"
            style={{ background: cuisine.bg, color: cuisine.color }}>
            {cuisine.label}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display text-lg font-semibold leading-snug line-clamp-2">{recipe.title}</h3>
        {hasDisliked && (
          <span data-testid={`disliked-warning-${recipe.id}`} className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ background: "#FBE7E3", color: "var(--at-brand)" }}>
            <AlertTriangle size={12} /> Contient {dislikedMain.join(", ")}
          </span>
        )}
        <div className="mt-2 flex items-center gap-4 text-sm" style={{ color: "var(--at-text-2)" }}>
          <span className="flex items-center gap-1"><Users size={15} /> {recipe.servings} pers.</span>
          {time > 0 && <span className="flex items-center gap-1"><Clock size={15} /> {time} min</span>}
        </div>
      </div>
    </button>
  );
}
